import './style.scss'
import React, { useContext, useEffect, useRef, useState } from 'react'
import { useLocation } from 'react-router-dom'
import { useHistory } from 'react-router-dom/cjs/react-router-dom.min'
import { useTranslation } from 'react-i18next'
import { Toast } from 'primereact/toast'
import { NoData } from 'Assets/Images'
import { GLOBAL_FREIGHT_RATES, ROUTE_STRINGS } from 'Utils/Constants'
import { useResizeHandler } from 'Utils/commonMethods'
import { coffeewebGetLocal, coffeewebStorageKeys } from 'Utils/LocalStorage_Handler'
import { CoffeeWebContext } from 'Context/CoffeeWebContext'
import { popupIcon } from 'Assets/Icons'
import CustomDropdown from 'Components/CustomDropdown'
import CreateAccountInfoModal from 'Components/UIComponent/CreateAccountInfoModal'
import apiAdapterCoffeeWeb from 'Services/apiAdapter_CoffeeWeb'
import { getAccessButtonAndInfo } from 'Utils/subscriptionAccess'
import AccordionForMobile from './Components/AccordionForMobile'
import AccordionHistoryData from './Components/AccordionHistoryData'

const GlobalFreightRates = () => {
  const { t } = useTranslation()

  const history = useHistory()
  const location = useLocation()
  const fullPath = location.pathname
  const pathSegments = fullPath.split('/')
  const desiredPath = pathSegments[2] || ''
  const { isLoading, setLoading, realtimeSubscriptionData } = useContext(CoffeeWebContext)
  const { isWebView } = useResizeHandler()
  const toast = useRef(null)
  const userDetails = coffeewebGetLocal(coffeewebStorageKeys.userDetails)
  const isUserLoggedIn = coffeewebGetLocal(coffeewebStorageKeys.isLoggedIn)
  const isAuthenticatedUser = Boolean(userDetails?.id || isUserLoggedIn)

  if (userDetails?.id && ROUTE_STRINGS.globalFreightRates !== fullPath) {
    history.replace({ pathname: ROUTE_STRINGS.globalFreightRates })
  }

  let menuLock

  if (userDetails && userDetails.businessGeneralMenus?.businessMenu) {
    const menu = userDetails.businessGeneralMenus?.businessMenu?.find(({ menuUrl }) => menuUrl?.includes(desiredPath))

    if (menu) {
      menuLock = menu.menulock
    }
  }

  const [freightRatesData, setFreightRatesData] = useState([])
  const [filteredFreightRatesData, setFilteredFreightRatesData] = useState([])
  const [freightRatesHistoryData, setFreightRatesHistoryData] = useState([])
  const [loadingPortList, setLoadingPortList] = useState([])
  const [destinationPortList, setDestinationPortList] = useState([])
  const [currentItem, setCurrentItem] = useState(null)
  const [currentPortItem, setCurrentPortItem] = useState(null)
  const [noDataFound, setNoDataFound] = useState(false)
  const [errorMessage, setErrorMessage] = useState(false)
  const [createAccountPopup, setCreateAccountPopup] = useState(false)
  const [applyBlurBackground, setApplyBlurBackground] = useState(false)
  const [isDialogModalOpen, setIsDialogModalOpen] = useState(false)
  const [portHeaderData, setPortHeaderData] = useState(null)
  const [hasInitialLoad, setHasInitialLoad] = useState(false)

  const subType = userDetails?.sub?.subType
  const realtimeSubscriptionName = (realtimeSubscriptionData?.subscriptionDetails?.SubscriptionName || '').trim()
  const hasRealtimeSubscription = Boolean(realtimeSubscriptionName)
  const isPlatinumUser = hasRealtimeSubscription ? realtimeSubscriptionName === 'Platinum' || realtimeSubscriptionName === 'Trial Platinum' : subType === 3

  const shouldLockContent = (userDetails?.id && !isPlatinumUser) || menuLock || !isAuthenticatedUser

  useEffect(() => {
    init()
  }, [])

  useEffect(() => {
    setApplyBlurBackground(shouldLockContent)
    setCreateAccountPopup(shouldLockContent)
  }, [shouldLockContent])

  // Apply blur to table body/image only when locked.
  const shouldShowBlur = applyBlurBackground && hasInitialLoad

  useEffect(() => {
    if (!shouldShowBlur) {
      const element = document.getElementById('global-freight-rates')

      if (element) {
        element.style.filter = 'none'
      }

      return
    }

    const timeoutId = setTimeout(() => {
      const element = document.getElementById('global-freight-rates')

      if (element) {
        element.style.filter = 'blur(3px)'
      }
    }, 0)

    return () => clearTimeout(timeoutId)
  }, [shouldShowBlur, freightRatesData.length])

  const init = () => {
    setLoading(true)

    try {
      handleSelectCountry(userDetails?.countryId || 102)
    } catch {
      setErrorMessage(true)
    }
  }

  const getFreightItemsByCountry = async (countryId) => {
    setLoading(true)
    try {
      const freightItemsResponse = await apiAdapterCoffeeWeb.getFreightItemsByCountry(countryId)

      setFreightRatesData(freightItemsResponse?.data?.returnLst?.freightRatesCountryData)
      setDestinationPortList(freightItemsResponse?.data?.returnLst?.destinationPorts)

      if (freightItemsResponse?.data.statusCode === 200) {
        if (freightItemsResponse?.data?.returnLst?.loadingPorts.length > 0) {
          // Group by country with all ports
          const modifiedData = freightItemsResponse?.data?.returnLst?.freightRatesCountryData?.map((countryObj) => ({
            country: countryObj.country,
            iso3: countryObj.iso3,
            ports: countryObj.globalFreightRateAdminDTOs
          }))

          setFreightRatesData(modifiedData)
          setFilteredFreightRatesData(modifiedData)

          // Set country list for loading ports dropdown
          const loadingPortModifiedList = freightItemsResponse?.data?.returnLst?.loadingPorts?.map((ele) => ({
            ...ele,
            id: ele.id.toString(),
            name: ele.nicename,
            flagsUrl: `https://coffeeweb.s3.ap-south-1.amazonaws.com/Masters/Flags/${ele.iso3}.svg`
          }))

          setLoadingPortList(loadingPortModifiedList)

          // Set currentItem to matching country or first item
          const matchingCountry = loadingPortModifiedList?.find((ele) => ele.id === countryId.toString())

          setCurrentItem(matchingCountry || loadingPortModifiedList[0])

          const allPort = {
            id: 0,
            flagsUrl: 'https://coffeeweb.s3.ap-south-1.amazonaws.com/Logo+Mark.png',
            nicename: 'All'
          }

          const portModifiedList = freightItemsResponse?.data.returnLst.destinationPorts?.map((ele) => ({
            ...ele,
            id: ele.id.toString(),
            name: ele.nicename,
            flagsUrl: `https://coffeeweb.s3.ap-south-1.amazonaws.com/Masters/Flags/${ele.iso3}.svg`
          }))

          const updatedDestinationPorts = [allPort, ...portModifiedList]

          setDestinationPortList(updatedDestinationPorts)

          // Preserve previously selected destination port if it still exists for the new loading/producing country; otherwise default to "All"
          let nextPortItem = allPort

          if (currentPortItem && currentPortItem.nicename !== 'All') {
            const matchedPort = updatedDestinationPorts.find((port) => port.id && currentPortItem.id && port.id.toString() === currentPortItem.id.toString())

            if (matchedPort) {
              nextPortItem = matchedPort
            }
          }

          setCurrentPortItem(nextPortItem)

          // Apply destination filter so the table shows data for the selected destination (or all)
          if (nextPortItem.nicename === 'All') {
            setFilteredFreightRatesData(modifiedData)
          } else {
            setFilteredFreightRatesData(modifiedData.filter((countryItem) => countryItem.iso3 === nextPortItem.iso3))
          }

          setNoDataFound(false)
        } else {
          setFreightRatesData([])

          setNoDataFound(true)
        }
      }
    } catch {
      setFreightRatesData([])
      setNoDataFound(true)
    } finally {
      setLoading(false)
      setHasInitialLoad(true)
    }
  }

  const getFreightHistoryData = async (loadingPort, destinationPort, liners, portHeader) => {
    setLoading(true)

    try {
      const freightHistoryDataResponse = await apiAdapterCoffeeWeb.getFreightHistoryData(loadingPort, destinationPort, liners)

      if (freightHistoryDataResponse?.data?.statusCode === 200 && freightHistoryDataResponse?.data?.returnLst?.data?.length > 0) {
        setFreightRatesHistoryData(freightHistoryDataResponse?.data)
        setPortHeaderData(portHeader)
        setIsDialogModalOpen(true)
      } else {
        setFreightRatesHistoryData([])
        showError()
      }
    } catch (error) {
      showError()
      console.error('Error fetching freight history data:', error)
    } finally {
      setLoading(false)
    }
  }

  const handleChange = (e) => {
    setCurrentItem(e.value)
    handleSelectCountry(e.value)
  }

  const handleSelectCountry = (countryIdOrObj) => {
    const id = countryIdOrObj && countryIdOrObj.id ? countryIdOrObj.id : countryIdOrObj

    if (id) {
      getFreightItemsByCountry(id)
    }
  }

  const { buttonText, infoMessage, infoMessage2, className, navigationPath, state } = getAccessButtonAndInfo({ realtimeSubscriptionData })

  const handleCloseModal = () => {
    setIsDialogModalOpen(false)
    setFreightRatesHistoryData([])
  }

  const handlePortChange = (e) => {
    setCurrentPortItem(e.value)

    // filter freight rates by selected port iso3; if 'All' selected, show all
    if (!e?.value) {
      setFilteredFreightRatesData(freightRatesData)

      return
    }

    if (e.value.nicename === 'All') {
      setFilteredFreightRatesData(freightRatesData)
    } else {
      // Filter countries by iso3
      setFilteredFreightRatesData(freightRatesData.filter((countryItem) => countryItem.iso3 === e.value.iso3))
    }
  }

  const showError = () => toast.current?.show({ severity: 'error', summary: 'Error', detail: t('UNABLE_TO_GET_HISTORY_FREIGHT_RATES'), life: 3000 })

  return (
    <>
      <div className="global-freight-rates-screens">
        {freightRatesHistoryData === null || freightRatesHistoryData.length === 0 ? <Toast ref={toast} className="freight-rates-toast" /> : <div></div>}

        <div className="global-freight-rates-headers">
          <div className="global-freight-rates-title">{t('GLOBAL_FREIGHT_RATES')}</div>
          <div className="dropdown">
            <div className="drop-down-content">
              <span className="dropdown-ports">{t('DESTINATION_COUNTRY')} :</span>

              <CustomDropdown onChange={handlePortChange} options={destinationPortList.length && destinationPortList} value={currentPortItem} optionLabel="nicename" setCurrentItem={setCurrentPortItem} errorState={errorMessage} showFlags filter placeholder={destinationPortList.length ? t('SELECT_LOADING_PORT') : 'No Data Found'} panelClassName="destination-country-panel" />
            </div>
            <div className="drop-down-content">
              <span className="dropdown-ports">{t('LOADING_COUNTRY')} :</span>

              <CustomDropdown onChange={handleChange} options={loadingPortList?.length && loadingPortList} value={currentItem} optionLabel="nicename" setCurrentItem={setCurrentItem} errorState={errorMessage} showFlags filter placeholder={loadingPortList.length ? t('SELECT_DESTINATION_PORT') : 'No Data Found'} panelClassName="producing-country-panel" />
            </div>
          </div>
        </div>

        {!isLoading && hasInitialLoad && freightRatesData.length > 0 && (
          <>
            <div className="global-freight-rates-body" id="global-freight-rates">
              {filteredFreightRatesData?.length > 0 &&
                filteredFreightRatesData.map((countryItem, countryIndex) => (
                  <React.Fragment key={countryIndex}>
                    <div className="global-freight-rates-table">
                      {/* COUNTRY HEADER */}
                      <div className="port-header">
                        <div className="port-header-content">
                          <img className="country-flag" src={`https://coffeeweb.s3.ap-south-1.amazonaws.com/Masters/Flags/${countryItem.iso3}.svg`} alt="country-flag" />
                          <div className="country-name">{countryItem.country}</div>
                        </div>
                      </div>
                      {/* RENDER ALL PORTS FOR THIS COUNTRY */}
                      {countryItem.ports?.map((portData, portIndex) => (
                        <div key={portIndex} className="global-freight-rates-table-data">
                          {isWebView ? (
                            <div className="freight-rates-table">
                              {portData.globalFreightRateData?.length > 0 && (
                                <table className="each-table">
                                  <thead className="freight-rates-table-header">
                                    <tr>
                                      <th></th>
                                      <th className="month-year">{portData.fullMonthYear}</th>
                                      <th></th>
                                      <th colSpan={2}></th>
                                      <th className="country-name">{portData.destinationPortName}</th>
                                      <th></th>
                                    </tr>
                                    <tr className="table-header-row">
                                      <th className="sl-th">{t('SL')}</th>
                                      <th className="loading-port-th">{t('LOADING_PORTS')}</th>
                                      <th className="destination-port-th">{t('DESTINATION_PORTS')}</th>
                                      <th colSpan={2}>{t('BETWEEN')}</th>
                                      <th className="transit-period-th">{t('TRANSIT_PERIOD')}</th>
                                      <th className="remarks-th">{t('REMARKS')}</th>
                                    </tr>
                                  </thead>

                                  <tbody className="freight-rates-table-body">
                                    {(() => {
                                      const globalFreightRateData = portData?.globalFreightRateData || []

                                      return globalFreightRateData.map((item, subIndex) => {
                                        const isFirstOfGroup = subIndex === 0 || item?.others !== globalFreightRateData[subIndex - 1]?.others

                                        let rowSpan = 0

                                        if (isFirstOfGroup) {
                                          for (let i = subIndex; i < globalFreightRateData.length; i += 1) {
                                            if (globalFreightRateData[i]?.others === item?.others) {
                                              rowSpan += 1
                                            } else {
                                              break
                                            }
                                          }
                                        }

                                        return (
                                          <tr key={item.id || subIndex}>
                                            <td>{subIndex + 1}</td>

                                            <td
                                              className="global-freight-rates-td"
                                              onClick={() => {
                                                if (!shouldShowBlur) {
                                                  getFreightHistoryData(item?.loadingPort, item?.destinationPort, item?.others, item)
                                                }
                                              }}
                                              style={{ pointerEvents: shouldShowBlur ? 'none' : 'auto', cursor: shouldShowBlur ? 'not-allowed' : 'pointer' }}
                                            >
                                              <div className="loading-port-div">
                                                <p className="loading-port-name">{item.loadingPort}</p>
                                                <img className="popup-icon" src={popupIcon} alt="popup-icon" />
                                              </div>
                                            </td>

                                            <td>{item.destinationPortName}</td>
                                            <td className="between-td">{item.freightRateFrom}</td>
                                            <td className="between-td">{item.freightRateTo}</td>
                                            <td className="transit-period">{item.transitPeriod}</td>
                                            {isFirstOfGroup && rowSpan > 0 && (
                                              <td className="remarks-td" rowSpan={rowSpan}>
                                                {item?.others}
                                              </td>
                                            )}
                                          </tr>
                                        )
                                      })
                                    })()}
                                  </tbody>
                                </table>
                              )}
                            </div>
                          ) : (
                            <AccordionForMobile data={portData} GDItem={currentItem} shouldShowBlur={shouldShowBlur} getFreightHistoryData={getFreightHistoryData} />
                          )}
                        </div>
                      ))}
                    </div>
                  </React.Fragment>
                ))}

              {noDataFound && !freightRatesData?.length && (
                <div className="no-data-found">
                  <img src={NoData} alt="No Data Found" className="no-data-image" />
                  <p className="no-data-message">{t('NO_DATA_FOUND_FOR_THIS_COUNTRY')}</p>
                </div>
              )}
            </div>

            <AccordionHistoryData isDialogModalOpen={isDialogModalOpen} handleCloseModal={handleCloseModal} freightRateHistoryData={freightRatesHistoryData} portHeaderData={portHeaderData} />
          </>
        )}

        {!isLoading && hasInitialLoad && freightRatesData.length === 0 && (
          <div className="global-freight-container" id="global-freight-rates">
            <img className="global-freight-image" src={GLOBAL_FREIGHT_RATES.image} alt="Global-Freight-Rates" />
          </div>
        )}

        {createAccountPopup && (
          <div className={`differential-create-account-info-modal-popup ${isAuthenticatedUser ? 'create-account-info-message' : 'guest-create-account-info-message'}`}>
            <CreateAccountInfoModal buttonText={buttonText} infoMessage={infoMessage} className={className} navigationPath={navigationPath} state={state} />
          </div>
        )}
      </div>
    </>
  )
}

export default GlobalFreightRates
